import { type Merchant } from "types/merchant";
import { useSelector } from "react-redux";
import { useApi } from "hooks/useAPI";
import { useEffect, useState } from "react";
import {
  CompareOption,
  SalesService,
  SalesSummaryData,
} from "services/SalesService";
import { getLocalTime } from "utils/getLocalTime";
import { RevenueWidget } from "views/app-views/dashboards/sales/StatisticWidgets/Revenue";
import { AdSpendWidget } from "views/app-views/dashboards/sales/StatisticWidgets/AdSpend";
import { AverageOrderVolume } from "views/app-views/dashboards/sales/StatisticWidgets/AverageOrderVolume";
import { ContributionMargin } from "views/app-views/dashboards/sales/StatisticWidgets/ContributionMargin";
import { MERWidget } from "views/app-views/dashboards/sales/StatisticWidgets/MERWidget";
import { Grid } from "@mui/material";
import VuiBox from "components/VuiBox";
import { ProfitWidget } from "views/app-views/dashboards/sales/StatisticWidgets/ProfitWidget";
import { useFeatureFlag } from "hooks/useFeatureFlag";
export const HeaderWidgets = () => {
  const { get } = useApi();
  const { isFeatureEnabled } = useFeatureFlag();
  const isProfitEnabled = isFeatureEnabled("enabled_profit_card", false);

  const selectedMerchant: Merchant = useSelector(
    (state: any) => state.merchants.selectedMerchant
  );
  const {
    startDate,
    endDate,
    compareOption,
  }: { startDate: Date; endDate: Date; compareOption: CompareOption } =
    useSelector((state: any) => state.common);
  const [loading, setLoading] = useState<boolean>(false);

  const [summary, setSummary] = useState<SalesSummaryData | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      const salesSummary = new SalesService(get);

      const { startDateString, endDateString } = getLocalTime(
        startDate,
        endDate
      );

      setLoading(true);
      const result = await salesSummary.getSummary({
        merchantId: selectedMerchant.id,
        start: startDateString,
        end: endDateString,
        compareOption: compareOption,
      });
      setLoading(false);
      setSummary(result);
    };

    if (selectedMerchant && startDate && endDate) {
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant, startDate, endDate, compareOption]);

  const gridSize = isProfitEnabled ? 2.4 : 3;

  return (
    <>
      <Grid item xs={12} sm={6} lg={gridSize}>
        <VuiBox mb={3}>
          <RevenueWidget
            loading={loading}
            value={summary?.revenue.value || 0}
            compareValue={summary?.revenue.compareValue || 0}
            currency={selectedMerchant.currency || "USD"}
          />
        </VuiBox>
      </Grid>
      <Grid item xs={12} sm={6} lg={gridSize}>
        <VuiBox mb={3}>
          <AdSpendWidget
            spendSummary={summary?.totalSpend}
            loading={loading}
            currency={selectedMerchant.currency || "USD"}
          />
        </VuiBox>
      </Grid>
      <Grid item xs={12} sm={6} lg={gridSize}>
        <VuiBox mb={3}>
          <ContributionMargin
            loading={loading}
            data={summary || undefined}
            currency={selectedMerchant.currency || "USD"}
          />
        </VuiBox>
      </Grid>
      <Grid item xs={12} sm={6} lg={gridSize}>
        <VuiBox mb={3}>
          <MERWidget
            loading={loading}
            value={summary?.mer.value || 0}
            compareValue={summary?.mer.compareValue || 0}
            currency={selectedMerchant.currency || "USD"}
          />
        </VuiBox>
      </Grid>
      {isProfitEnabled && (
        <Grid item xs={12} sm={6} lg={2.4}>
          <VuiBox mb={3}>
            <ProfitWidget />
          </VuiBox>
        </Grid>
      )}
    </>
  );
};
